'use client';

import React, { useState } from 'react';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '@/components/ui/dialog';

interface WaitlistDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

export function WaitlistDialog({ isOpen, onClose }: WaitlistDialogProps) {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [company, setCompany] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [emailError, setEmailError] = useState('');

    const validateEmail = (value: string): string => {
        if (!value.trim()) return 'Email is required.';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address.';
        return '';
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const emailErr = validateEmail(email);
        if (emailErr) {
            setEmailError(emailErr);
            return;
        }
        setError('');
        setLoading(true);

        try {
            const res = await fetch('/api/waitlist', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, name, company }),
            });

            const data = await res.json();

            if (!res.ok) {
                setError(data.error || 'Something went wrong.');
                return;
            }

            setSuccess(true);
        } catch {
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        // Reset form state when closing
        setEmail('');
        setName('');
        setCompany('');
        setError('');
        setEmailError('');
        setSuccess(false);
        setLoading(false);
        onClose();
    };

    return (
        <Dialog isOpen={isOpen} onClose={handleClose} preventClose={loading} size="sm">
            <DialogHeader
                icon={success ? 'check_circle' : 'rocket_launch'}
                iconColor={success ? 'text-emerald-400' : 'text-emerald-400'}
                iconBg="bg-emerald-500/10"
                title={success ? "You're on the list!" : 'Join the Waitlist'}
                subtitle={
                    success
                        ? ""
                        : 'Be the first to experience AI-powered marketing.'
                }
                onClose={handleClose}
                closeDisabled={loading}
            />

            {success ? (
                <DialogBody>
                    <div className="text-center py-4">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                            <span className="material-symbols-outlined text-3xl text-emerald-400">
                                celebration
                            </span>
                        </div>
                        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                            Thanks for your interest in NectarAI! We&apos;ll send you an
                            invite as soon as we&apos;re ready.
                        </p>
                    </div>
                </DialogBody>
            ) : (
                <form onSubmit={handleSubmit}>
                    <DialogBody className="space-y-4">
                        {/* Email */}
                        <div>
                            <label
                                htmlFor="waitlist-email"
                                className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5"
                            >
                                Email <span className="text-red-400">*</span>
                            </label>
                            <input
                                id="waitlist-email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    if (emailError) setEmailError(validateEmail(e.target.value));
                                }}
                                onBlur={() => setEmailError(validateEmail(email))}
                                placeholder="you@company.com"
                                className={`w-full px-3 py-2.5 rounded-lg bg-[var(--bg-app)] border text-[var(--text-primary)] placeholder:text-[var(--text-muted)] text-sm focus:outline-none focus:ring-2 transition-all ${emailError
                                    ? 'border-red-500/60 focus:ring-red-500/40 focus:border-red-500/40'
                                    : 'border-[var(--border-subtle)] focus:ring-emerald-500/40 focus:border-emerald-500/40'
                                    }`}
                            />
                            {emailError && (
                                <p className="mt-1.5 text-xs text-red-400">{emailError}</p>
                            )}
                        </div>

                        {/* Name */}
                        <div>
                            <label
                                htmlFor="waitlist-name"
                                className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5"
                            >
                                Name
                            </label>
                            <input
                                id="waitlist-name"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Jane Doe"
                                className="w-full px-3 py-2.5 rounded-lg bg-[var(--bg-app)] border border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500/40 transition-all"
                            />
                        </div>

                        {/* Company */}
                        <div>
                            <label
                                htmlFor="waitlist-company"
                                className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5"
                            >
                                Company
                            </label>
                            <input
                                id="waitlist-company"
                                type="text"
                                value={company}
                                onChange={(e) => setCompany(e.target.value)}
                                placeholder="Acme Inc."
                                className="w-full px-3 py-2.5 rounded-lg bg-[var(--bg-app)] border border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500/40 transition-all"
                            />
                        </div>

                        {/* Error */}
                        {error && (
                            <div className="flex items-center gap-2 rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-2.5 text-sm text-red-400">
                                <span className="material-symbols-outlined text-base">error</span>
                                {error}
                            </div>
                        )}
                    </DialogBody>

                    <DialogFooter>
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={loading}
                            className="px-4 py-2.5 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-5 py-2.5 rounded-lg font-medium bg-emerald-500 hover:bg-emerald-400 text-[#0A0A0A] transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-[0_0_20px_rgba(16,185,129,0.3)]"
                        >
                            {loading && (
                                <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                            )}
                            {loading ? 'Joining…' : 'Join Waitlist'}
                        </button>
                    </DialogFooter>
                </form>
            )}
        </Dialog>
    );
}
